import { useState } from "react";
import { Checkbox } from "swing-components";
import { translate } from "utils/string-table";

import {
  ModalSubFeedbackBody,
  ModalSubFeedbackFooter,
  ModalSubFeedbackHeader,
  NeutralData,
  NeutralFeedbackReasons,
  NeutralStep,
} from "~components/ModalSubFeedback";

/********* Types ********/
type UpdatedNeutralReasons = Omit<NeutralData, "comment">;

type ModalSubFeedbackContentNeutralReasonsProps = {
  goBackToRatingStep: () => void;
  neutralData: NeutralData;
  onDismiss: () => void;
  setNeutralData: (data: NeutralData) => void;
  setNeutralStep: (step: NeutralStep) => void;
  subName?: string;
};

/********* Component ********/
export function ModalSubFeedbackContentNeutralReasons(
  props: ModalSubFeedbackContentNeutralReasonsProps,
) {
  const { goBackToRatingStep, neutralData, onDismiss, setNeutralData, setNeutralStep, subName } =
    props;

  /********* Hooks ********/
  const [_updatedReasons, _setUpdatedReasons] = useState<UpdatedNeutralReasons>({
    reasons: neutralData.reasons,
    otherReason: neutralData.otherReason ?? "",
  });

  /********* Handlers ********/
  function handleCheckboxChange(reason: NeutralFeedbackReasons, isChecked: boolean) {
    _setUpdatedReasons((prev) => {
      const newReasons = isChecked
        ? [...prev.reasons, reason]
        : prev.reasons.filter((r) => r !== reason);

      // If "Other" is unchecked, remove otherReasons
      const newOtherReason = reason === "REASON_OTHER" && !isChecked ? undefined : prev.otherReason;

      return { ...prev, reasons: newReasons, otherReason: newOtherReason };
    });
  }

  function handleOtherReason(reason: string) {
    _setUpdatedReasons((prev) => ({ ...prev, otherReason: reason }));
  }

  function updateNeutralDataAndProceed(nextStep?: NeutralStep) {
    const updatedData = {
      ...neutralData,
      ..._updatedReasons,
      // Don't add otherReason field if it's empty
      ...(_updatedReasons.otherReason?.trim() && { otherReason: _updatedReasons.otherReason }),
    };
    // Persist updated feedback data before navigating
    setNeutralData(updatedData);

    if (!nextStep) {
      goBackToRatingStep();
      return;
    }

    setNeutralStep(nextStep);
  }

  /********* Constants ********/
  const selectedReasons = Object.keys(NeutralReasonsSelections) as NeutralFeedbackReasons[];

  const isNextDisabled =
    !_updatedReasons.reasons?.length ||
    (_updatedReasons.reasons?.includes("REASON_OTHER") && !_updatedReasons.otherReason?.trim());

  /********* Render ********/
  return (
    <>
      <ModalSubFeedbackHeader title={translate("GIVE_FEEDBACK_LABEL")} onDismiss={onDismiss} />
      <ModalSubFeedbackBody>
        <p css={{ fontWeight: 600 }}>{translate("MSF_NEUTRAL_FEEDBACK_REASON_LABEL", subName)}</p>
        <p>{translate("LABEL_SELECT_ALL_THAT_APPLY")}</p>
        <div css={{ marginTop: 16 }}>
          {selectedReasons.map((reason) => {
            const isChecked = _updatedReasons.reasons.includes(reason);
            return (
              <Checkbox
                key={reason}
                label={NeutralReasonsSelections[reason]}
                isChecked={isChecked}
                onCheckedChange={(checked: boolean) => handleCheckboxChange(reason, checked)}
                hasBorderBottom={reason !== "REASON_OTHER"}
                hasInputField={reason === "REASON_OTHER"}
                textAreaProps={{
                  isRequired: true,
                  label: translate("MSF_REASON_OTHER_TEXT_INPUT_LABEL"),
                  value: _updatedReasons.otherReason,
                  onChange: (reason: string) => handleOtherReason(reason),
                  placeholder: translate(
                    "MSF_NEUTRAL_NEGATIVE_REASON_OTHER_TEXT_INPUT_PLACEHOLDER",
                  ),
                }}
              />
            );
          })}
        </div>
      </ModalSubFeedbackBody>
      <ModalSubFeedbackFooter
        buttonPrimary={{
          onClick: () => updateNeutralDataAndProceed("comments"),
          isDisabled: isNextDisabled,
          text: translate("LABEL_NEXT"),
        }}
        buttonSecondary={{
          onClick: () => updateNeutralDataAndProceed(),
          isDisabled: false,
          text: translate("LABEL_BACK"),
        }}
      />
    </>
  );
}

// Note: Order matters here - it controls the order the UI displays the reasons
export const NeutralReasonsSelections: Record<NeutralFeedbackReasons, string> = {
  REASON_ATTITUDE: translate("MSF_NEUTRAL_FEEDBACK_REASON_ATTITUDE"),
  REASON_DISENGAGED: translate("MSF_NEUTRAL_FEEDBACK_REASON_DISENGAGED"),
  REASON_DIRECTIONS: translate("MSF_NEUTRAL_FEEDBACK_REASON_DIRECTIONS"),
  REASON_MANAGEMENT: translate("MSF_NEUTRAL_FEEDBACK_REASON_MANAGEMENT"),
  REASON_PHONE: translate("MSF_NEUTRAL_FEEDBACK_REASON_PHONE"),
  REASON_RELIABILITY: translate("MSF_NEUTRAL_FEEDBACK_REASON_RELIABILITY"),
  REASON_OTHER: translate("MSF_REASON_OTHER"),
};
